<template>
  <el-row :gutter="15">
    <el-col
      :xs="24"
      :sm="8"
      :md="6"
      :lg="4"
      :xl="3"
    >
      <el-card
        shadow="never"
        header="Notification Providers"
      >
        <el-menu
          :default-active="activeModel"
          class="border-0"
          @select="setActiveModel"
        >
          <el-menu-item
            v-for="model in modelMenu"
            :key="model.key"
            :index="model.key"
          >
            <FontAwesomeIcon
              :icon="model.icon"
              size="lg"
              fixed-width
            />
            <span class="pl-3">{{ model.label }}</span>
          </el-menu-item>
        </el-menu>
      </el-card>
    </el-col>

    <el-col
      :xs="4"
      :sm="4"
      :md="4"
      :lg="4"
      :xl="4"
    >
      <Filters
        :key="activeModel"
        v-model="activeFilters"
        :resource="activeResource"
        :primary-fields="activePrimaryFields"
        @apply="applyFilters"
      />
    </el-col>

    <el-col
      :xs="24"
      :sm="24"
      :md="17"
      :lg="17"
      :xl="17"
    >
      <el-card
        v-loading="isLoading"
        shadow="never"
      >
        <template #header>
          <span>{{ activeModelConfig.tableTitle }}</span>
        </template>

        <el-alert
          v-if="modelErrorByModel[activeModel]"
          :title="modelErrorByModel[activeModel]"
          type="warning"
          :closable="false"
          class="mb-3"
        />

        <el-table
          :data="records"
          stripe
          class="w-100"
        >
          <el-table-column
            v-for="column in activeModelConfig.columns"
            :key="column.key"
            :label="column.label"
            :width="column.width"
            :min-width="column.minWidth"
            show-overflow-tooltip
          >
            <template slot-scope="scope">
              {{ formatCell(scope.row, column) }}
            </template>
          </el-table-column>

          <el-table-column
            label=""
            width="200"
            align="right"
          >
            <template slot-scope="scope">
              <el-button
                size="mini"
                @click.native="openRecord(scope.row)"
              >
                View
              </el-button>
              <el-button
                size="mini"
                @click.native="resend(scope.row)"
              >
                Resend
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="text-center pt-3">
          <el-pagination
            :current-page.sync="paginationByModel[activeModel].page"
            :page-size.sync="paginationByModel[activeModel].limit"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="paginationByModel[activeModel].total"
            @size-change="reload"
            @current-change="reload"
          />
        </div>
      </el-card>
    </el-col>

    <el-dialog
      :title="selectedRecord ? `Log Entry — ${$moment(selectedRecord.created_at).format('YYYY-MM-DD HH:mm:ss')}` : 'Log Entry'"
      width="65vw"
      top="3vh"
      :visible.sync="isDialogVisible"
      @closed="selectedRecord = null"
    >
      <template v-if="selectedRecord">
        <el-alert
          v-if="isConsignmentResource"
          type="info"
          :closable="false"
          class="mb-4"
          title=""
        >
          <router-link
            :to="consignmentRoute"
            target="_blank"
          >
            <FontAwesomeIcon
              :icon="['fal', 'external-link']"
              class="mr-1"
            />
            View Consignment
          </router-link>
        </el-alert>

        <div
          v-for="field in recordFields"
          :key="field.key"
          class="mb-4"
        >
          <p
            class="text-muted mb-1"
            style="font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;"
          >
            {{ field.label }}
          </p>

          <pre
            v-if="field.isJson"
            style="background: #f4f4f5; border-radius: 4px; font-size: 12px; line-height: 1.5; margin: 0; max-height: 320px; overflow: auto; padding: 10px; white-space: pre-wrap; word-break: break-all;"
          >{{ field.formatted }}</pre>

          <span
            v-else
            style="word-break: break-all;"
          >{{ field.value || '-' }}</span>
        </div>
      </template>
    </el-dialog>
  </el-row>
</template>

<script>
import Filters from '@/components/Filters/Simplified'

const MODEL_CONFIG = {
  customeriolog: {
    menuLabel: 'Customer.io',
    tableTitle: 'Customer.io Notification Logs',
    primaryFields: ['body', 'response_body', 'event_name', 'response_status', 'created_at'],
    columns: [
      { key: 'created_at', label: 'Created', width: 170, format: 'datetime' },
      { key: 'event_name', label: 'Event', minWidth: 160 },
      { key: 'response_status', label: 'Status', width: 80 },
      { key: 'resource', label: 'Resource', minWidth: 130 },
      { key: 'response_body', label: 'Response Body', minWidth: 150 },
      { key: 'body', label: 'Request Body', minWidth: 260 }
    ]
  },
  messagebirdlog: {
    menuLabel: 'MessageBird',
    tableTitle: 'MessageBird Notification Logs',
    primaryFields: ['body', 'response_body', 'event_name', 'response_status', 'created_at'],
    columns: [
      { key: 'created_at', label: 'Created', width: 170, format: 'datetime' },
      { key: 'event_name', label: 'Event', minWidth: 160 },
      { key: 'response_status', label: 'Status', width: 100 },
      { key: 'resource', label: 'Resource', minWidth: 130 },
      { key: 'response_body', label: 'Response Body', minWidth: 260 },
      { key: 'body', label: 'Request Body', minWidth: 260 }
    ]
  },
  sendgridlog: {
    menuLabel: 'SendGrid',
    tableTitle: 'SendGrid Notification Logs',
    primaryFields: ['body', 'response_body', 'event_name', 'response_status', 'created_at'],
    columns: [
      { key: 'created_at', label: 'Created', width: 170, format: 'datetime' },
      { key: 'event_name', label: 'Event', minWidth: 160 },
      { key: 'response_status', label: 'Status', width: 80 },
      { key: 'resource', label: 'Resource', minWidth: 130 },
      { key: 'response_body', label: 'Response Body', minWidth: 150 },
      { key: 'body', label: 'Request Body', minWidth: 260 }
    ]
  }
}

export default {
  name: 'TrackingNotificationLogs',
  components: {
    Filters
  },
  data () {
    return {
      title: 'Tracking Notification Logs',
      breadcrumb: {
        name: 'Tracking Notification Logs',
        route: { name: 'configuration-tracking-notification-logs' }
      },
      actions: [
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: () => this.reload()
        }
      ],
      activeModel: 'customeriolog',
      recordsByModel: {
        customeriolog: [],
        messagebirdlog: [],
        sendgridlog: []
      },
      filtersByModel: {
        customeriolog: [],
        messagebirdlog: [],
        sendgridlog: []
      },
      paginationByModel: {
        customeriolog: {
          page: 1,
          limit: 20,
          total: 0
        },
        messagebirdlog: {
          page: 1,
          limit: 20,
          total: 0
        },
        sendgridlog: {
          page: 1,
          limit: 20,
          total: 0
        }
      },
      isLoadingByModel: {
        customeriolog: false,
        messagebirdlog: false,
        sendgridlog: false
      },
      modelErrorByModel: {
        customeriolog: '',
        messagebirdlog: '',
        sendgridlog: ''
      },
      selectedRecord: null,
      isDialogVisible: false
    }
  },
  computed: {
    modelMenu () {
      return [
        {
          key: 'customeriolog',
          label: MODEL_CONFIG.customeriolog.menuLabel,
          icon: ['fal', 'paper-plane']
        },
        {
          key: 'messagebirdlog',
          label: MODEL_CONFIG.messagebirdlog.menuLabel,
          icon: ['fal', 'comment-alt-dots']
        },
        {
          key: 'sendgridlog',
          label: MODEL_CONFIG.sendgridlog.menuLabel,
          icon: ['fal', 'envelope']
        }
      ]
    },
    activeModelConfig () {
      return MODEL_CONFIG[this.activeModel]
    },
    activePrimaryFields () {
      return this.activeModelConfig.primaryFields || []
    },
    activeResource () {
      return this.$sdk[this.activeModel]
    },
    activeFilters: {
      get () {
        return this.filtersByModel[this.activeModel]
      },
      set (value) {
        this.filtersByModel[this.activeModel] = value
      }
    },
    records () {
      return this.recordsByModel[this.activeModel]
    },
    isLoading () {
      return this.isLoadingByModel[this.activeModel]
    },
    isConsignmentResource () {
      return (
        this.selectedRecord &&
        typeof this.selectedRecord.resource === 'string' &&
        this.selectedRecord.resource.toLowerCase() === 'consignment'
      )
    },
    consignmentRoute () {
      if (!this.isConsignmentResource) return null
      return {
        name: 'freight-consignments-v3-consignment',
        params: { consignmentId: this.selectedRecord.resource_uuid }
      }
    },
    recordFields () {
      if (!this.selectedRecord) return []
      const sdkClass = this.$sdk[this.activeModel]
      const fieldKeys = sdkClass && sdkClass.fields ? Object.keys(sdkClass.fields) : []
      return fieldKeys.map(key => {
        const value = this.selectedRecord[key]
        const isAlreadyObject = value !== null && typeof value === 'object'
        const parsed = isAlreadyObject ? value : this.tryParseJson(value)
        const isJson = parsed !== null
        const displayValue = (value === null || typeof value === 'undefined' || value === '') ? '-' : `${value}`
        return {
          key,
          label: key.replace(/_/g, ' '),
          value: displayValue,
          isJson,
          formatted: isJson ? JSON.stringify(parsed, null, 2) : null
        }
      })
    }
  },
  created () {
    this.load()
  },
  methods: {
    setActiveModel (modelKey) {
      this.activeModel = modelKey
      if (!this.recordsByModel[this.activeModel].length && !this.modelErrorByModel[this.activeModel]) {
        this.load()
      }
    },
    applyFilters () {
      this.paginationByModel[this.activeModel].page = 1
      this.load()
    },
    resend (record) {
      const sdkModel = this.$sdk[this.activeModel]
      if (sdkModel && typeof sdkModel.resend === 'function') {
        sdkModel.resend(record.id)
          .then(() => {
            this.$message.success('Notification resent successfully.')
            this.load()
          })
          .catch(() => {
            this.$message.error('Failed to resend notification.')
          })
      } else {
        this.$message.error('Resend action is not available for this notification type.')
      }
    },
    openRecord (row) {
      this.selectedRecord = row
      this.isDialogVisible = true
    },
    tryParseJson (value) {
      if (typeof value !== 'string' || value.trim() === '') return null
      try {
        const parsed = JSON.parse(value)
        if (typeof parsed === 'object' && parsed !== null) return parsed
      } catch (e) {}
      return null
    },
    reload () {
      this.load()
    },
    async load () {
      console.log(`Loading ${this.activeModel} logs with filters:`, this.activeFilters)
      console.log(this.$sdk[this.activeModel])
      const sdkModel = this.$sdk[this.activeModel]

      if (!sdkModel || typeof sdkModel.list !== 'function') {
        this.modelErrorByModel[this.activeModel] = `${this.activeModelConfig.menuLabel} log model is not available in this build.`
        this.recordsByModel[this.activeModel] = []
        this.paginationByModel[this.activeModel].total = 0
        return
      }

      this.modelErrorByModel[this.activeModel] = ''
      this.isLoadingByModel[this.activeModel] = true

      try {
        const response = await sdkModel.list(builder => {
          builder.order = 'created_at:desc'
          builder.page = this.paginationByModel[this.activeModel].page
          builder.limit = this.paginationByModel[this.activeModel].limit
          builder.filters = this.activeFilters
        })

        this.recordsByModel[this.activeModel] = response
        this.paginationByModel[this.activeModel].total = response.total || 0
      } catch (error) {
        this.modelErrorByModel[this.activeModel] = `Unable to load ${this.activeModelConfig.menuLabel} logs.`
      } finally {
        this.isLoadingByModel[this.activeModel] = false
      }
    },
    formatCell (row, column) {
      const value = row[column.key]

      if (column.format === 'datetime') {
        return value ? this.$moment(value).format('YYYY-MM-DD HH:mm:ss') : '-'
      }

      if (value === null || typeof value === 'undefined' || value === '') {
        return '-'
      }

      if (Array.isArray(value) || typeof value === 'object') {
        return JSON.stringify(value)
      }

      return `${value}`
    }
  }
}
</script>
